import { useMutation, useQuery } from "@tanstack/react-query";

import type { SandboxAdminQueryParams } from "shell/console/types";

import { extractRestErrorMessage } from "shell/platform/utils/utils";

import type { SandboxAdminError } from "./types";

import { sandboxAdminFetch } from "../client";

const throwError = async (response: Response): Promise<never> => {
  const body = await response.json().catch(() => ({}));
  const message = extractRestErrorMessage(body) ?? response.statusText;
  throw Object.assign(new Error(message), { status: response.status, body });
};

export const useSandboxAdminQuery = <TData>(
  queryKey: ReadonlyArray<unknown>,
  path: string,
  options?: {
    params?: SandboxAdminQueryParams;
    enabled?: boolean;
  },
) =>
  useQuery<TData, SandboxAdminError>({
    queryKey,
    enabled: options?.enabled,
    queryFn: async () => {
      const response = await sandboxAdminFetch(path, {
        params: options?.params,
      });
      if (!response.ok) await throwError(response);
      if (response.status === 204) return undefined as TData;
      return response.json() as Promise<TData>;
    },
  });

export const useSandboxAdminMutation = <TData, TVariables = void>(
  path: string | ((data: TVariables) => string),
  options?: {
    method?: string;
    getBody?: (data: TVariables) => unknown;
  },
) =>
  useMutation<TData, SandboxAdminError, TVariables>({
    mutationFn: async data => {
      const resolvedPath = typeof path === "function" ? path(data) : path;
      const response = await sandboxAdminFetch(resolvedPath, {
        method: options?.method ?? "PATCH",
        body: options?.getBody
          ? options.getBody(data)
          : (data as Record<string, unknown>),
      });
      if (!response.ok) await throwError(response);
      if (response.status === 204) return undefined as TData;
      return response.json() as Promise<TData>;
    },
  });
